import React, { useEffect, useState } from "react";
import { Col, Table, Spinner, Badge, Row, Container, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { FaArrowLeft } from "react-icons/fa";

const PPSIView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [purchaseData, setPurchaseData] = useState(null);
    const [exportchargescurrency, setExportchargescurrency] = useState(0);
    const [rows, setRows] = useState([]);

    const fetchPurchaseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}purchaseReturn/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;
            setPurchaseData(data);
            setExportchargescurrency(data.exportchargescurrency || 0);

            if (data.purchaseReturnItem && data.purchaseReturnItem.length > 0) {
                setRows(data.purchaseReturnItem.map(item => ({
                    description: item.product?.description || '-',
                    productremarks: item.productremarks || "",
                    quantity: item.quantity,
                    unitratecurrency: item.unitratecurrency,
                    totalcurrency: item.totalcurrency,
                    exchangerate: item.exchangerate,
                    unitRateInr: item.unitrateinr,
                    totalInr: item.totalinr,
                    hsnsacno: item.hsnsacno,
                    partno: item.partno,
                    serialnumber: item.serialnumber,
                    modelnumber: item.modelnumber,
                    tagnumber: item.tagnumber,
                    moc: item.moc || "",
                    itemcode: item.itemcode || "",
                    uom: item.uom,
                    currency: item.currency || "$ (USD)"
                })));
            }
        } catch (error) {
            console.error("Error fetching purchase return data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch purchase return data. Please try again.",
            });
            navigate("/pindex/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchaseData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getAddressString = (address) => {
        if (!address) return '-';
        return [
            address.supplieraddress,
            address.city?.cityname,
            address.state?.statename,
            address.pincode,
            address.country?.countryname
        ].filter(Boolean).join(', ');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            case 'Completed': return 'info';
            case 'Open': return 'warning';
            default: return 'light';
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0.00';
        return parseFloat(value).toFixed(2);
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!purchaseData) {
        return <div className="alert alert-danger text-center mt-4">Purchase return data not found</div>;
    }

    // Calculate totals
    const netTotalINR = rows.reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0);
    const netTotalCurrency = rows.reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0);
    const exportChargesINR = (exportchargescurrency * (purchaseData.purchaseexchangerate || 0));
    const grandTotalINR = netTotalINR + exportChargesINR;
    const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-danger mb-0">INTERNATIONAL PURCHASE RETURN - VIEW</h5>
                        <div>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </Button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-danger ps-2">
                                <small className="text-muted">Return No</small>
                                <div className="fw-bold">{purchaseData.purchasereturnno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Return Date</small>
                                <div className="fw-bold">{formatDate(purchaseData.purchasereturndate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Currency</small>
                                <div className="fw-bold">{purchaseData.purchasecurrency || '$ (USD)'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge bg={getStatusBadge(purchaseData.status)} className="px-2 py-1">
                                        {purchaseData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Invoice Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Invoice Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Purchase Invoice No</small>
                            <div className="fw-bold">{purchaseData.purchaseinvoiceno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Purchase Invoice Date</small>
                            <div className="fw-bold">{formatDate(purchaseData.purchaseinvoicedate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Exchange Rate (₹/Currency)</small>
                            <div className="fw-bold">{purchaseData.purchaseexchangerate || '0.00'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Supplier Invoice No</small>
                            <div className="fw-bold">{purchaseData.supplierinvoiceno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Supplier Invoice Date</small>
                            <div className="fw-bold">{formatDate(purchaseData.supplierinvoicedate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Other Reference</small>
                            <div className="fw-bold">{purchaseData.otherreference || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Supplier Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Supplier Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Company Name</small>
                            <div className="fw-bold">{purchaseData.supplier?.companyname || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Contact Person</small>
                            <div className="fw-bold">{purchaseData.contactperson || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">GST No</small>
                            <div className="fw-bold">{purchaseData.supplier?.gstno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Phone</small>
                            <div className="fw-bold">{purchaseData.contactno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Email</small>
                            <div className="fw-bold">{purchaseData.email || '-'}</div>
                        </Col>
                        <Col md={12}>
                            <small className="text-muted">Address</small>
                            <div className="fw-bold">{getAddressString(purchaseData.supplierMultipleAddress)}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Delivery Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Delivery Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <small className="text-muted">Branch</small>
                            <div className="fw-bold">{purchaseData.branch?.branchname || '-'}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Warehouse</small>
                            <div className="fw-bold">{purchaseData.warehouse?.warehousename || '-'}</div>
                        </Col>
                        <Col md={12}>
                            <small className="text-muted">Delivery Address</small>
                            <div className="fw-bold">{purchaseData.shipto || '-'}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Return Items */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Return Items</h6>
                </div>
                <div className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Product Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (FC)</th>
                                    <th className="fw-bold py-2 text-end">Total (FC)</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (₹)</th>
                                    <th className="fw-bold py-2 text-end">Total (₹)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{row.description || '-'}</div>
                                            {row.productremarks && row.productremarks !== '-' && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {row.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td>{row.serialnumber || '-'}</td>
                                        <td>{row.modelnumber || '-'}</td>
                                        <td>{row.tagnumber || '-'}</td>
                                        <td>{row.moc || '-'}</td>
                                        <td>{row.itemcode || '-'}</td>
                                        <td className="text-center">{row.quantity || '0'}</td>
                                        <td className="text-center">{row.uom || '-'}</td>
                                        <td className="text-end">{formatNumber(row.unitratecurrency)}</td>
                                        <td className="text-end fw-semibold">{formatNumber(row.totalcurrency)}</td>
                                        <td className="text-end">{formatCurrency(row.unitRateInr)}</td>
                                        <td className="text-end fw-semibold">{formatCurrency(row.totalInr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Calculation Summary */}
            <Row className="mb-3">
                <Col lg={6} className="ms-auto">
                    <div className="shadow-sm border rounded bg-white">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
                        </div>
                        <div className="p-3">
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Net Total (INR):</span>
                                <span className="fw-bold">{formatCurrency(netTotalINR)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Net Total (FC):</span>
                                <span className="fw-bold">{formatNumber(netTotalCurrency)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Export Charges (FC):</span>
                                <span className="fw-bold">{formatNumber(exportchargescurrency)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="fw-semibold">Export Charges (INR):</span>
                                <span className="fw-bold">{formatCurrency(exportChargesINR)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                <span className="fw-bold">Grand Total (INR)</span>
                                <span className="fw-bold text-primary">{formatCurrency(grandTotalINR)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2">
                                <span className="fw-bold">Grand Total (FC)</span>
                                <span className="fw-bold text-success">{formatNumber(grandTotalCurrency)}</span>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Button>
            </div>
        </Container>
    );
};

export default PPSIView;